import { cn } from "@/lib/cn";

export interface FilaGantt {
  clave: string;
  nombre: string;
  /** "02/11/2026 al 13/11/2026 · 10 días hábiles". */
  detalle: string;
  /** 0 = grupo (un rubro, con su barra resumen); 1 = tarea. */
  nivel: 0 | 1;
  /** Posición y largo en % del largo de la obra. */
  izquierda: number;
  ancho: number;
  hito: boolean;
  critico: boolean;
  /** Si viene, el nombre es un botón (ej. editar la tarea). */
  onElegir?: () => void;
}

export interface MarcaGantt {
  posicion: number; // % del largo
  texto: string;
}

export interface GanttProps {
  etiqueta: string;
  filas: FilaGantt[];
  /** Las divisiones del eje de tiempo (meses, o días sin fecha de inicio). */
  marcas: MarcaGantt[];
  /** Posición de hoy en %, o `null`. */
  hoy?: number | null;
}

/**
 * Diagrama de Gantt de toda la obra: una fila por grupo (barra resumen) y por tarea (barra, roja si es crítica; un
 * rombo si es un hito), sobre un eje de tiempo con sus divisiones y la línea de hoy. En el celular se desplaza de
 * costado con la columna de nombres fija.
 */
export function Gantt({ etiqueta, filas, marcas, hoy = null }: GanttProps) {
  const divisiones = marcas.map((m) => <div key={`${m.posicion}-${m.texto}`} aria-hidden className="absolute inset-y-0 w-px bg-borde" style={{ left: `${m.posicion}%` }} />);
  return (
    <div className="overflow-x-auto rounded-tarjeta border border-borde bg-superficie">
      <div className="min-w-[48rem]">
        <div className="grid grid-cols-[14rem_1fr] border-b border-borde text-chico text-texto-suave">
          <div className="sticky left-0 z-10 bg-superficie px-3 py-2 font-medium">Tarea</div>
          <div className="relative h-8" aria-hidden>
            {marcas.map((m) => (
              <span key={`${m.posicion}-${m.texto}`} className="absolute top-2 -translate-x-1/2 whitespace-nowrap" style={{ left: `${m.posicion}%` }}>
                {m.texto}
              </span>
            ))}
          </div>
        </div>
        <ul aria-label={etiqueta}>
          {filas.map((f) => (
            <li key={f.clave} className={cn("grid grid-cols-[14rem_1fr] border-b border-borde last:border-0", f.nivel === 0 && "bg-superficie-2")}>
              <div className={cn("sticky left-0 z-10 min-w-0 py-1.5 pr-3", f.nivel === 0 ? "bg-superficie-2 pl-3" : "bg-superficie pl-6")}>
                {f.onElegir ? (
                  <button type="button" onClick={f.onElegir} title={f.nombre} className="block w-full truncate text-left text-chico text-primario underline-offset-2 hover:underline">
                    {f.nombre}
                  </button>
                ) : (
                  <span title={f.nombre} className={cn("block truncate text-chico", f.nivel === 0 && "font-semibold")}>
                    {f.nombre}
                  </span>
                )}
              </div>
              <div className="relative h-8">
                {divisiones}
                <div
                  role="img"
                  aria-label={`${f.nombre}: ${f.detalle}`}
                  title={f.detalle}
                  className={cn(
                    "absolute top-1/2 -translate-y-1/2",
                    f.hito ? "size-3 -translate-x-1/2 rotate-45" : "rounded-full",
                    !f.hito && (f.nivel === 0 ? "h-2" : "h-4"),
                    f.nivel === 0 ? "bg-texto-suave" : f.critico ? "bg-peligro" : f.hito ? "bg-texto" : "bg-primario",
                  )}
                  style={f.hito ? { left: `${f.izquierda}%` } : { left: `${f.izquierda}%`, width: `${f.ancho}%` }}
                />
                {hoy !== null && <div data-testid="hoy" aria-hidden className="absolute inset-y-0 w-px bg-alerta" style={{ left: `${hoy}%` }} />}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
